<template>
  <section class="construction-types">
    <TextInput :formValues="sectorNameTitle" @input="update($event, 'name')" />
    <article class="construction-types__types-switcher">
      <form>
        <radio-button
          v-for="type in constructionTypes"
          :key="type.id"
          :selected="constructionType"
          :radioGroup="type"
          @input="update($event, 'constructionType', 'type')"
        />
      </form>
    </article>
    <article
      v-if="!sector.constructionType.isCombinedLocation"
      class="construction-types__attributes"
    >
      <template v-for="dropdown in dropdowns">
        <OptionDropdown
          v-if="dropdown.viewCondition"
          :updateKey="`${constructionType}-${sector.id}`"
          :dropdown="dropdown"
          :key="dropdown.id"
          @update="update($event, 'constructionType', `${dropdown.id}`)"
        />
      </template>
    </article>
  </section>
</template>

<script>
import IconMixin from '@/components/mixin/IconMixin'
import TextInput from '@/components/dump/TextInput'
import RadioButton from '@/components/dump/RadioButton'
import OptionDropdown from '@/components/dump/OptionDropdown'
import { uuid } from 'vue-uuid'
import { mapActions, mapGetters } from 'vuex'
import { ConstructionTypes } from '@/utils/types'
import DropdownConfigsMixin from '@/components/mixin/DropdownConfigsMixin.vue'

export default {
  name: 'ConstructionTypes',
  mixins: [IconMixin, DropdownConfigsMixin],
  props: ['sector'],
  components: { RadioButton, TextInput, OptionDropdown },
  data() {
    return {
      emptyOptions: [{ id: 0, value: '' }]
    }
  },
  watch: {
    isRoomAlgorithm() {
      if (this.isRoomAlgorithm) {
        this.getSystemForRooms(this.sectorIndex)
      }
    }
  },
  computed: {
    ...mapGetters({
      getConstructionTypes: 'getConstructionTypes',
      getRooms: 'getRooms'
    }),
    sectorIndex() {
      return this.$store.state.sectors.findIndex(sector => sector.id === this.sector.id)
    },
    sectorNameTitle() {
      return {
        label: this.$t('message.constructionTypes.input.label'),
        sectorName: this.sector.name ?? '',
        id: uuid.v1()
      }
    },
    constructionType() {
      return this.sector.constructionType.type
    },
    currentConstructionTypeId() {
      return this.sector.constructionType.type
    },
    buildingType() {
      return this.sector.constructionType.buildingType
    },
    location() {
      return this.sector.constructionType.location
    },
    constructionTypes() {
      if (this.getConstructionTypes.length === 0) return this.getConstructionTypes
      const typesOrder = [
        ConstructionTypes.wall,
        ConstructionTypes.floor,
        ConstructionTypes.ceiling
      ]

      return this.getConstructionTypes.map((type, index, array) => {
        const orderedType = array.find(
          orderedItem => orderedItem.constructionId === typesOrder[index]
        )

        return {
          id: orderedType.constructionId,
          constructionId: orderedType.constructionId,
          img: this.images[orderedType.constructionId],
          value: orderedType.constructionType,
          name: this.$t('message.constructionTypes.types.title')
        }
      })
    },
    buildingTypes() {
      if (this.constructionType && this.getConstructionTypes.length) {
        const buildings = this.getConstructionTypes?.find(
          type => type.constructionId === this.constructionType
        )?.buildings
        return buildings ? this.getOptions(buildings, true) : this.emptyOptions
      }

      return this.emptyOptions
    },
    locations() {
      if (!this.buildingType) {
        return this.emptyOptions
      }

      const locations = this.buildingTypes?.find(location => location.name === this.buildingType)
        ?.locations

      return this.buildingType && locations ? this.getOptions(locations, true) : this.emptyOptions
    },
    isRoomAlgorithm() {
      return (
        this.sector.constructionType.isCombinedLocation && this.sector.constructionType.location?.id
      )
    },
    images() {
      return {
        wall: require('@/assets/img/walls.png'),
        ceiling: require('@/assets/img/ceiling.png'),
        floor: require('@/assets/img/floors.png')
      }
    }
  },
  methods: {
    ...mapActions({
      getSystemForRooms: 'getSystemForRooms'
    }),
    update(evt, property, subproperty) {
      this.sector.isCalculated || this.sector.system
        ? this.editSector(evt, property, subproperty)
        : this.setFirstStepValues(evt, property, subproperty)
    },
    resetConstructionTypesValues(property, evt) {
      ['type', 'isCombinedLocation', ...this.dropdowns.map(item => item.id)].forEach(
        subproperty => {
          return this.setConstructionTypesValues(
            property,
            subproperty,
            this.getInitialValue(subproperty, evt)
          )
        }
      )
      this.setConstructionTypesValues('roomSystems', null, null)
      this.$emit('checkStepValidation')
    },
    getInitialValue(subproperty, evt) {
      switch (subproperty) {
        case 'type':
          return evt.newValue
        case 'isCombinedLocation':
          return false
        case 'buildingType':
          return this.buildingTypes[0].value
        default:
          return null
      }
    },
    setConstructionTypesValues(property, subproperty, value) {
      this.$store.commit('UPDATE_SECTOR', {
        index: this.sectorIndex,
        property,
        subproperty,
        value
      })
    },
    editSector(evt, property, subproperty) {
      this.setFirstStepValues(evt, property, subproperty)
      this.$emit('checkStepValidation')
      this.setConstructionTypesValues('isCalculated', null, false)
    },
    setFirstStepValues(evt, property, subproperty) {
      subproperty === 'type'
        ? this.resetConstructionTypesValues(property, evt)
        : this.setConstructionTypesValues(property, subproperty, evt.newValue)

      if (subproperty === 'buildingType') {
        this.setConstructionTypesValues('constructionType', 'location', null)
      }
    }
  }
}
</script>

<style scoped lang="sass">
.construction-types
  width: 100%
  &__types-switcher
    form
      display: grid
      grid-template-rows: auto
      +media((grid-template-columns: (0: 1fr, 768: repeat(2, 1fr))))
      +media((grid-gap: (0: rem(10), 768: rem(20))))
      margin-bottom: rem(30)
  &__attributes
    display: grid
    grid-template-columns: 2fr 1fr
    +media((grid-template-areas: (0: 'buildingType buildingType' 'rating rating' 'location location', 992: 'buildingType buildingType rating' 'location location location')))
</style>
